import { useEffect, useRef, useState } from "react";
import { Mic, Paperclip, Square, FileText } from "lucide-react";
import { cn } from "@/lib/utils";

// Jeff 10/29: capture gains a voice mode and attachments — WITHOUT growing back
// into a second form. A mic button and a paperclip sit under the textarea;
// voice → recorded locally (MediaRecorder), shipped to /api/idea-attach, and
// transcribed on Kit's box (mlx-whisper) so the TEXT lands in the same box.
// Files (PDF/doc/image) attach to the idea when it's submitted.
const MAX_BYTES = 25 * 1024 * 1024;

const b64 = (blob) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(",", 2)[1]);
    r.onerror = reject;
    r.readAsDataURL(blob);
  });

const upload = async (kind, file) => {
  const body = {
    kind,
    filename: file.name || (kind === "voice" ? "voice-memo.webm" : "attachment"),
    mime: file.type || (kind === "voice" ? "audio/webm" : "application/octet-stream"),
    bytes_b64: await b64(file),
  };
  const res = await fetch("/api/idea-attach", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const detail = await res.json().catch(() => ({}));
    throw new Error(detail.detail || `Upload failed (${res.status})`);
  }
  return res.json();
};

export const CaptureExtras = ({ onTranscript, onAttached, registerAttachIds, disabled }) => {
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [notice, setNotice] = useState(null);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const pollRef = useRef(null);
  const attachIdsRef = useRef([]);

  // The idea row doesn't exist until Jeff hits "Add" — every clip/file uploaded
  // meanwhile is unbound. Hand the accumulated ids to the parent so the submit
  // binds them onto the row (Jeff 10/30: memo + MD uploaded pre-submit, then
  // stranded because nothing bound them).
  useEffect(() => {
    registerAttachIds?.(() => attachIdsRef.current);
  }, [registerAttachIds]);

  useEffect(() => () => {  // unmount hygiene: stop mic + polling
    if (recRef.current?.state === "recording") recRef.current.stop();
    recRef.current?.stream?.getTracks().forEach((t) => t.stop());
    if (pollRef.current) clearInterval(pollRef.current);
  }, []);

  const pollTranscript = (taskId) => {
    let tries = 0;
    pollRef.current = setInterval(async () => {
      tries += 1;
      try {
        const r = await fetch(`/api/idea-transcribe/${taskId}`);
        if (!r.ok) return;
        const d = await r.json();
        if (d.text) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setTranscribing(false);
          setNotice(null);
          onTranscript?.(d.text);
        } else if (d.error) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setTranscribing(false);
          setNotice(d.error);
        } else if (tries > 120) {  // ~6 min ceiling
          clearInterval(pollRef.current);
          pollRef.current = null;
          setTranscribing(false);
          setNotice("Transcription is taking too long — the memo is saved; Kit can push the text later.");
        }
      } catch { /* keep polling */ }
    }, 3000);
  };

  const startRec = async () => {
    setNotice(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        setRecording(false);
        if (blob.size < 800) { setNotice("That clip was too short — hold the mic a little longer."); return; }
        if (blob.size > MAX_BYTES) { setNotice("That clip is over the 25MB cap."); return; }
        setTranscribing(true);
        setNotice("Saved. Transcribing on Kit's box…");
        try {
          const { attach_id } = await upload("voice", blob);
          attachIdsRef.current.push(attach_id);
          const r = await fetch("/api/idea-transcribe", {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ attach_id }),
          });
          if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "Transcription queue failed");
          pollTranscript((await r.json()).task_id);
        } catch (err) {
          setTranscribing(false);
          setNotice(err.message || "The clip is saved but transcription didn't start.");
        }
      };
      recRef.current = rec;
      rec.start();
      setRecording(true);
    } catch {
      setNotice("Microphone unavailable — check the browser's mic permission for this site.");
    }
  };

  const stopRec = () => recRef.current?.state === "recording" && recRef.current.stop();

  const pickFile = async () => {
    setNotice(null);
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".pdf,.doc,.docx,.txt,.md,image/*";
    input.onchange = async () => {
      const f = input.files?.[0];
      if (!f) return;
      if (f.size > MAX_BYTES) { setNotice(`${f.name} is over the 25MB cap.`); return; }
      try {
        setNotice(`Uploading ${f.name}…`);
        const { attach_id } = await upload("file", f);
        attachIdsRef.current.push(attach_id);
        setNotice(null);
        onAttached?.({ name: f.name, size: f.size });
      } catch (err) {
        setNotice(err.message || "Upload failed.");
      }
    };
    input.click();
  };

  return (
    <div data-testid="idea-capture-extras" className="flex items-center gap-2">
      {recording ? (
        <button
          type="button" data-testid="idea-mic-stop" aria-label="Stop recording"
          onClick={stopRec} disabled={disabled}
          className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-os-red bg-os-red/10 text-os-red hover:bg-os-red/20"
        >
          <Square className="h-3.5 w-3.5" strokeWidth={2} />
        </button>
      ) : (
        <button
          type="button" data-testid="idea-mic-start" aria-label="Record voice"
          onClick={startRec} disabled={disabled || transcribing}
          title="Record your idea — Kit turns it into text"
          className="inline-flex h-8 w-8 items-center justify-center rounded-control border border-os-border text-os-ink-2 hover:bg-os-wash hover:text-os-ink disabled:opacity-40"
        >
          <Mic className="h-4 w-4" strokeWidth={1.75} />
        </button>
      )}
      <button
        type="button" data-testid="idea-attach-toggle" aria-label="Attach a file"
        onClick={pickFile} disabled={disabled}
        title="Attach a PDF, document, or image"
        className="inline-flex h-8 w-8 items-center justify-center rounded-control border border-os-border text-os-ink-2 hover:bg-os-wash hover:text-os-ink disabled:opacity-40"
      >
        <Paperclip className="h-4 w-4 -rotate-45" strokeWidth={1.75} />
      </button>
      {transcribing && (
        <span data-testid="idea-transcribing" className="font-mono text-[0.5625rem] uppercase tracking-[0.08em] text-os-amber motion-safe:animate-pulse">
          transcribing
        </span>
      )}
      {notice && <span data-testid="idea-attach-notice" className="text-[0.75rem] text-os-ink-3">{notice}</span>}
    </div>
  );
};

export default CaptureExtras;
